import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useModulesConfig } from '~/data/company';
import {
  AdminWorkspaceRoles,
  Roles,
  getRoleTranslationKey,
} from '~/data/user/role';
import {
  getAvailableWidgets,
  getWorkspaceRolesbyUserRoles,
} from '~/scenes/MyWorkspace/components/Widgets/data';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  WorkspaceSettings,
  getRolesDefaultWidgets,
} from '~/scenes/MyWorkspace/userSettings';
import { isRoleHRAdmin } from '~/services/Permissions';

type UseDefaultWorkspaceTemplates = {
  allWorkspaceTemplates: {
    [key in AdminWorkspaceRoles]: WorkspaceSettings;
  };
  getWorkspaceTemplateByRole: (role: AdminWorkspaceRoles) => WorkspaceSettings;
};

export const useWorkspaceTemplates = (): UseDefaultWorkspaceTemplates => {
  const { t } = useTranslation();
  const { isModuleTabEnabled, isModuleEnabled } = useModulesConfig();

  const roles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(roles);

  const allWorkspaceTemplates = useMemo((): {
    [key in Roles]: WorkspaceSettings;
  } => {
    const addedRoles = roles.includes(Roles.EMPLOYEE)
      ? roles
      : [...roles, Roles.EMPLOYEE];
    const workspaceRoles = isHRAdmin
      ? Object.keys(AdminWorkspaceRoles)
      : addedRoles;
    return Object.fromEntries(
      Object.values(workspaceRoles).map((role): [Roles, WorkspaceSettings] => {
        const availableWidgets = getAvailableWidgets({
          workspaceRoles: getWorkspaceRolesbyUserRoles([role]),
          isModuleEnabled,
          isModuleTabEnabled,
        });

        return [
          role as Roles,
          {
            id: role,
            name: t(getRoleTranslationKey(role as Roles)),
            widgets: getRolesDefaultWidgets(role as Roles).filter((ws) =>
              availableWidgets.includes(ws.widget),
            ),
            type: WorkspaceType.Default,
            restrictToRoles: [],
          },
        ];
      }),
    ) as { [key in Roles]: WorkspaceSettings };
  }, [t, roles, isHRAdmin, isModuleTabEnabled, isModuleEnabled]);

  const getWorkspaceTemplateByRole = useCallback(
    (role: AdminWorkspaceRoles): WorkspaceSettings => {
      return allWorkspaceTemplates[role];
    },
    [allWorkspaceTemplates],
  );

  return {
    allWorkspaceTemplates,
    getWorkspaceTemplateByRole,
  };
};
